import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import {
    ITCIApplicationDeploymentProvider,
    ITCIApplicationMonitoring,
    ITCIApplicationMonitoringProvider,
    ITCIApplicationMonitoringResponse,
    ITCIApplicationsMonitoringRequest,
    ITCIMonitoringInstance,
    TCI_APP_DETAIL_MONITORING_MENU_TYPE,
    TCI_APP_MONITORING_REQUEST_TYPE,
    TCIApplicationDeploymentNotification,
    TCITenantAppDetailMonitoring,
    TCITenantSelectorType,
    TCITenantAppType
} from "@tci/common";
import { NgToastrService } from "../../../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import { NgSubmenuLeftService } from "../../ng-submenu-left/ng-submenu-left.service";
import { AbstractTCITenantComponent } from "../../../ng-tenant/tci-tenant-component";
import { BaseAppDetailMenuComponentInput, BaseAppDetailMenuComponentOutput } from "../../ng-app-details-menu";
import { TciTenantModelService } from "../../../ng-tenant/tci-tenant.model";
import { TciTenantService } from "../../../ng-tenant/tci-tenant.service";

import { Subscription } from "rxjs";
import * as lodash from "lodash";

@Component({
    selector: TCITenantSelectorType.TCI_APP_DETAIL_MONITORING.toString(),
    templateUrl: "./base-app-detail-monitoring.component.html",
    styleUrls: ["./base-app-detail-monitoring.component.less"]
})
export class BaseAppDetailMonitoringComponent<E, I extends BaseAppDetailMenuComponentInput, O extends BaseAppDetailMenuComponentOutput<E>>
    extends AbstractTCITenantComponent<E, I, O> implements OnInit, OnDestroy {
    private submenuLeftRouteId: string;
    private currentRoute: string;

    flowMetricsInput: BaseAppDetailMenuComponentInput;
    subMenuItemInput: BaseAppDetailMenuComponentInput;

    // Subscriptions collection
    private subscriptions: Subscription[] = [];

    // Instances options
    canShowInstanceSelector = false;
    instancesOptions: ITCIMonitoringInstance[] = [];
    instanceSelected: ITCIMonitoringInstance;
    instanceActive = false;
    monitoringSvc: ITCIApplicationMonitoringProvider;

    constructor(
        private route: ActivatedRoute,
        public menuSvc: NgSubmenuLeftService,
        private tenantSvc: TciTenantService,
        private tenantModelSvc: TciTenantModelService,
        public toastr: NgToastrService
    ) {
        super();
    }

    ngOnInit(): void {
        this.currentRoute = this.input.componentOption.getRouteId();

        this.tenantSvc.onInit().subscribe(() => {
            const tenant = this.tenantModelSvc.getTenant(this.input.application.type);
            const errMsg = "No monitoring provider for this application type: " + this.input.application.type.toString();
            if (tenant) {
                const provider = tenant.getMonitoringServiceProvider();
                if (provider) {
                    this.monitoringSvc = provider;

                    // Register the app at the service
                    this.startApp();

                    // Track route updates for submenu
                    this.subscriptions.push(
                        this.route.params.subscribe(params => {
                            this.submenuLeftRouteId = params["submenuleft"];
                            if (this.submenuLeftRouteId) {
                                this.menuSvc.validateMenuItem(this.input.application, this.currentRoute, this.submenuLeftRouteId)
                                    .filter(valid => valid)
                                    .subscribe(() => this.mountMenuItemComponent());
                            }
                        }));
                    // Mount "Flow Metrics Collection"
                    this.mountFlowMetricsCollection();

                    const deployprovider = tenant.getAppDeploymentProvider();
                    if (deployprovider) {
                        this.subscriptions.push(
                            deployprovider.getStatusSubject()
                                .filter(res => res.appId === this.input.application.id)
                                .filter(res =>
                                    [
                                        TCIApplicationDeploymentNotification.NOTIFICATION_REFRESH,
                                        TCIApplicationDeploymentNotification.NOTIFICATION_STATUS
                                    ].includes(res.notificationType)
                                )
                                .subscribe(res => {
                                    this.getInstances();
                                })
                        );
                    } else {
                        console.warn("No deploy provider for this application type: "
                            + this.input.application.type.toString());
                    }

                } else {
                    console.warn(errMsg);
                }
            } else {
                console.warn(errMsg);
            }
        });
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());

        // Unregister the app
        this.monitoringSvc.stopAppMonitoringSubscription(this.getProvisionerRequest(null))
            .subscribe(res => {
                if (res) {
                    console.log("Stopped monitoring app");
                } else {
                    console.warn("Monitor stop sent FALSE");
                }
            });
    }

    // Returns an object to be used as provisioner's methods input argument
    private getProvisionerRequest(request_type: TCI_APP_MONITORING_REQUEST_TYPE): ITCIApplicationsMonitoringRequest {
        return {
            request_type,
            application: this.input.getApplication(),
            appId: this.input.application.id,
            tenantType: this.input.tenantAppType
        };
    }

    private startApp(): void {
        // Listen to provider changes
        this.monitoringSvc.getAppMonitoringSubject(TCI_APP_MONITORING_REQUEST_TYPE.APP_INSTANCE_SELECTED) // Instance selected
            .subscribe(res => this.instanceSelected = (<ITCIApplicationMonitoring>res.result).selectedInstance);

        this.monitoringSvc.startAppMonitoringSubscription(this.getProvisionerRequest(null))
            .subscribe(res => {
                if (res) {
                    this.getInstances();
                } else {
                    // console.warn("WS Monitor > start sent FALSE");
                }
            });
    }

    private getInstances(): void {
        const input = this.getProvisionerRequest(TCI_APP_MONITORING_REQUEST_TYPE.APP_INSTANCE_LIST);
        this.monitoringSvc.canShowInstanceSelector(input)
            .subscribe(canShowInstanceSelector => {
                this.canShowInstanceSelector = canShowInstanceSelector;
                if (canShowInstanceSelector) {
                    this.monitoringSvc.getAppInstances(input)
                        .map((res: ITCIApplicationMonitoringResponse) => res.result as ITCIApplicationMonitoring)
                        .subscribe(res => {
                            // Transform the list to something easy for the template
                            this.instancesOptions = lodash.map(res.instances, val => val);

                            // Select instance only if different to current or if current is not present
                            if (!this.instanceSelected || !this.instancesOptions.map(i => i.key).includes(this.instanceSelected.key)) {
                                this.selectInstance(lodash.first(this.instancesOptions));
                            }
                        });
                } else {
                    this.instancesOptions = null;
                    this.monitoringSvc.setSelectedInstance(null);
                }
            });
    }

    private mountFlowMetricsCollection(): void {
        if (TCITenantAppType.flogo.equals(this.input.application.type)) { // Metrics collection only for Flogo
            this.flowMetricsInput = new BaseAppDetailMenuComponentInput(
                this.input.tenantAppType,
                TCITenantSelectorType.TCI_MENU_APP_MONITORING_METRICS_COLLECTION,
                this.input.application,
                this.input.userService,
                this.input.featureToggleService,
                this.input.mode
            );
        }
    }

    private mountMenuItemComponent(): void {
        if (this.input && this.input.application) {
            const subMenuItemSelector = TCITenantAppDetailMonitoring.getAllMenuItems(TCI_APP_DETAIL_MONITORING_MENU_TYPE.MAIN_MENU)
                .find(item => item.getRouteId() === this.submenuLeftRouteId);

            this.subMenuItemInput = new BaseAppDetailMenuComponentInput(
                this.input.tenantAppType,
                subMenuItemSelector.getComponentSelectorType(),
                this.input.application,
                this.input.userService,
                this.input.featureToggleService,
                this.input.mode,
                this.input.componentOption
            );
        }
    }

    selectInstance(opt: ITCIMonitoringInstance) {
        if (!this.instanceSelected || opt.key !== this.instanceSelected.key) {
            this.monitoringSvc.setSelectedInstance(opt);
        }
        this.instanceActive = false;
    }
}
